import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { Sport } from '@/feature/event-details-sbk/types';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { cacheControlHeader } from '@/utils/constants-platform-specific';
import { useQuery } from '@tanstack/react-query';

export const createNavigationKeys = (product: Product) => {
    const all = [product, 'navigation'] as const;
    return { all };
};

export type NavigationKeys = ReturnType<typeof createNavigationKeys>;

export const useNavigationKeys = (): NavigationKeys => {
    const product = useJurisdictionStore(state => state.product);
    return useMemo(() => createNavigationKeys(product), [product]);
};

export type NavigationSubGroup = {
    type: 'league';
    id: string;
    label: string;
    league_id: string;
};

type NavigationEntryBase = {
    id: string;
    label: string;
    icon_url: string | null;
    is_featured: boolean;
    event_display_count: number;
    event_count: number;
    live_event_count: number;
    sport: Sport;
    sub_groups: NavigationSubGroup[];
};

export type NavigationSportEntry = NavigationEntryBase & {
    type: 'sport';
    sport_id: string;
};

export type NavigationLeagueEntry = NavigationEntryBase & {
    type: 'league';
    league_id: string;
};

export type NavigationCompetitionEntry = NavigationEntryBase & {
    type: 'competition';
    starts_at: string | null;
    ends_at: string | null;
};

export type NavigationEntry = NavigationSportEntry | NavigationLeagueEntry | NavigationCompetitionEntry;

export type GetNavigationResponse = {
    data: NavigationEntry[];
};

export const getNavigation = async (): Promise<GetNavigationResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/navigation`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            ...cacheControlHeader,
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

export const useNavigationEntries = () => {
    const navigationKeys = useNavigationKeys();
    return useQuery({
        queryKey: navigationKeys.all,
        queryFn: getNavigation,
        select: data => data.data,
    });
};
